import React, { useEffect, useRef, useState } from "react";
import { Filter, Link2, RefreshCw, Search } from "lucide-react";
import { useWarehouseStatus, formatWarehouseValue } from "../traits/warehouse-api";
import {
  useWarehouseEvents,
  useWarehouseIdentityGraph,
  useWarehouseLookup,
  useWarehouseMergeHistory,
  useWarehouseProfileDetail,
  useWarehouseProfiles,
  formatWarehouseTimestamp,
  warehouseProfileName,
} from "./warehouse-api";

// Profile Explorer — real data only, from the connected data warehouse (see
// docs/data-warehouse-traits-integration.md). The mock/demo explorer that
// used to live here (behind a "Live Data" toggle) has been removed per
// direct request; ./api.ts's mock generator is still used elsewhere (the
// Traits builder's sample-profile preview step), so it isn't deleted, just
// no longer imported by this page.

const PER_PAGE = 20;

// Identity Graph and Merge History are real-only capabilities from
// docs/data-warehouse-traits-integration.md.
type WarehouseTabType = "overview" | "traits" | "events" | "ids" | "graph" | "merges";
const IDENTIFIER_TYPES = ["email", "phone", "anonymous_id"] as const;

const friendlyTraitLabel = (v: string) => v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const relativeTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const ProfileExplorer: React.FC = () => {
  // Real data only — see docs/data-warehouse-traits-integration.md.
  // statusLoading distinguishes "still checking" from "checked, and it's
  // genuinely not connected" so the page doesn't flash a "not connected"
  // message before the status call has even resolved.
  const { data: warehouseStatus, loading: statusLoading } = useWarehouseStatus();
  const warehouseAvailable = !!(warehouseStatus?.enabled && warehouseStatus?.configured);
  const [wPage, setWPage] = useState(1);
  const [wSelectedId, setWSelectedId] = useState<string | null>(null);
  const [wActiveTab, setWActiveTab] = useState<WarehouseTabType>("overview");
  const [wEventsPage, setWEventsPage] = useState(1);
  const [lookupIdentifier, setLookupIdentifier] = useState("");
  const [lookupType, setLookupType] = useState<(typeof IDENTIFIER_TYPES)[number]>("email");
  const [lookupState, setLookupState] = useState<{ notFound: boolean; error: string | null }>({ notFound: false, error: null });

  // Live list search/filters — separate from the mock explorer's above (the
  // fields differ: real warehouse profiles have gender/country/income_band,
  // not region/network/churn-risk).
  const [wSearch, setWSearch] = useState("");
  const [wDebouncedSearch, setWDebouncedSearch] = useState("");
  const [wGender, setWGender] = useState("");
  const [wCountry, setWCountry] = useState("");
  const [wIncomeBand, setWIncomeBand] = useState("");
  const [wFiltersOpen, setWFiltersOpen] = useState(false);
  const wFiltersRef = useRef<HTMLDivElement>(null);
  const wActiveFilterCount = [wGender, wCountry, wIncomeBand].filter(Boolean).length;

  useEffect(() => {
    const t = setTimeout(() => setWDebouncedSearch(wSearch), 300);
    return () => clearTimeout(t);
  }, [wSearch]);
  useEffect(() => {
    setWPage(1);
  }, [wDebouncedSearch, wGender, wCountry, wIncomeBand]);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (wFiltersRef.current && !wFiltersRef.current.contains(e.target as Node)) setWFiltersOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const {
    data: wList,
    loading: wListLoading,
    error: wListError,
    retry: wListRetry,
  } = useWarehouseProfiles(
    warehouseAvailable
      ? { page: wPage, perPage: PER_PAGE, search: wDebouncedSearch, gender: wGender || undefined, country: wCountry || undefined, incomeBand: wIncomeBand || undefined }
      : null
  );

  useEffect(() => {
    if (!warehouseAvailable || !wList) return;
    const stillPresent = wList.data.some((p) => p.profile_id === wSelectedId);
    if (!stillPresent) {
      setWSelectedId(wList.data[0]?.profile_id ?? null);
      setWActiveTab("overview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wList, warehouseAvailable]);

  const { data: wDetail, loading: wDetailLoading, notFound: wDetailNotFound, error: wDetailError } = useWarehouseProfileDetail(warehouseAvailable ? wSelectedId : null);
  // Per the doc: identity graph / events / merge history only load once the
  // profile detail request has already succeeded.
  const wDetailReady = warehouseAvailable && !!wDetail;
  const { data: wGraph, loading: wGraphLoading } = useWarehouseIdentityGraph(wSelectedId, wDetailReady);
  const { data: wEvents, loading: wEventsLoading } = useWarehouseEvents(wSelectedId, wDetailReady, wEventsPage, PER_PAGE);
  const { data: wMerges, loading: wMergesLoading } = useWarehouseMergeHistory(wSelectedId, wDetailReady);
  const { loading: lookupLoading, lookup } = useWarehouseLookup();

  const selectWarehouseProfile = (id: string) => {
    setWSelectedId(id);
    setWActiveTab("overview");
    setWEventsPage(1);
  };

  // Explicit submit only — never a partial identifier or client-side filter
  // over identifiers, per the doc's migration guidance.
  const submitLookup = async () => {
    const identifier = lookupIdentifier.trim();
    if (!identifier) return;
    setLookupState({ notFound: false, error: null });
    const { result, notFound, error } = await lookup(identifier, lookupType);
    setLookupState({ notFound, error });
    if (result) selectWarehouseProfile(result.profile_id);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile Explorer</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Search and explore unified customer profiles with complete history across all channels.
        </p>
      </div>

      {!statusLoading && !warehouseAvailable ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400 text-center px-6">
          Connect a data warehouse (Settings) to see live profiles here.
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar — warehouse profile list + identifier lookup */}
          <aside className="w-[320px] flex-none border-r border-gray-200 dark:border-gray-800 flex flex-col bg-gray-50 dark:bg-gray-800/60">
            <div className="p-3 border-b border-gray-200 dark:border-gray-800 space-y-2">
              <div className="flex items-center gap-1.5">
                <select
                  value={lookupType}
                  onChange={(e) => setLookupType(e.target.value as (typeof IDENTIFIER_TYPES)[number])}
                  className="px-2 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100"
                >
                  {IDENTIFIER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Exact identifier value…"
                    value={lookupIdentifier}
                    onChange={(e) => setLookupIdentifier(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitLookup()}
                    className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={submitLookup}
                  disabled={lookupLoading || !lookupIdentifier.trim()}
                  className="px-3 py-2 text-xs font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Go
                </button>
              </div>
              {lookupState.notFound && <p className="text-xs text-amber-600 dark:text-amber-400">No profile found for that identifier.</p>}
              {lookupState.error && <p className="text-xs text-rose-600 dark:text-rose-400">{lookupState.error}</p>}
              <p className="text-[11px] text-gray-400 dark:text-gray-500">Exact match only — this searches on submit, not as you type.</p>
            </div>

            <div className="p-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search profiles"
                  value={wSearch}
                  onChange={(e) => setWSearch(e.target.value)}
                  maxLength={100}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div className="relative" ref={wFiltersRef}>
                <button
                  type="button"
                  onClick={() => setWFiltersOpen((o) => !o)}
                  className="relative flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                >
                  <Filter size={16} />
                  {wActiveFilterCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">
                      {wActiveFilterCount}
                    </span>
                  )}
                </button>
                {wFiltersOpen && (
                  <div className="absolute right-0 z-20 mt-1 w-64 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg p-3 space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Gender</label>
                      <select
                        value={wGender}
                        onChange={(e) => setWGender(e.target.value)}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 dark:text-gray-100"
                      >
                        <option value="">All genders</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Country</label>
                      <input
                        value={wCountry}
                        onChange={(e) => setWCountry(e.target.value)}
                        placeholder="e.g. Nigeria"
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Income band</label>
                      <select
                        value={wIncomeBand}
                        onChange={(e) => setWIncomeBand(e.target.value)}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 dark:text-gray-100"
                      >
                        <option value="">All bands</option>
                        <option value="Low">Low</option>
                        <option value="Lower-Middle">Lower-Middle</option>
                        <option value="Middle">Middle</option>
                        <option value="Upper-Middle">Upper-Middle</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                    {wActiveFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setWGender("");
                          setWCountry("");
                          setWIncomeBand("");
                        }}
                        className="text-xs text-blue-600 dark:text-blue-400 underline underline-offset-2"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {wListError ? (
                <div className="p-4 text-center">
                  <p className="text-xs text-rose-600 dark:text-rose-400 mb-2">{wListError}</p>
                  <button type="button" onClick={wListRetry} className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 underline underline-offset-2">
                    <RefreshCw size={12} /> Retry
                  </button>
                </div>
              ) : wListLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 animate-pulse">
                    <div className="h-3.5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                    <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                ))
              ) : wList && wList.data.length > 0 ? (
                wList.data.map((p) => (
                  <div
                    key={p.profile_id}
                    onClick={() => selectWarehouseProfile(p.profile_id)}
                    className={`px-4 py-3 border-b border-gray-200 dark:border-gray-800 cursor-pointer transition-colors ${
                      p.profile_id === wSelectedId ? "bg-blue-50 dark:bg-blue-500/10" : "hover:bg-white dark:hover:bg-gray-800"
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={p.profile_id}>
                      {warehouseProfileName(p)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {p.country || "—"} · {p.trait_count} trait{p.trait_count === 1 ? "" : "s"}
                    </div>
                    <div className="flex gap-1 flex-wrap mt-1.5">
                      {p.identifier_types.map((t) => (
                        <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              ) : wDebouncedSearch || wActiveFilterCount > 0 ? (
                <p className="p-6 text-center text-sm text-gray-400">No profiles match this search/filter.</p>
              ) : (
                <p className="p-6 text-center text-sm text-gray-400">
                  No tenant-scoped profiles yet. Most warehouse profiles aren't assigned to an organization yet — an empty result here is expected, not an error.
                </p>
              )}
            </div>

            {wList && wList.total_pages > 1 && (
              <div className="p-2.5 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <button
                  type="button"
                  disabled={wPage <= 1}
                  onClick={() => setWPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <span>
                  Page {wList.page} of {wList.total_pages} · {wList.total} profiles
                </span>
                <button
                  type="button"
                  disabled={wPage >= wList.total_pages}
                  onClick={() => setWPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </aside>

          {/* Detail Panel — warehouse profile */}
          <main className="flex-1 flex flex-col overflow-hidden">
            {wDetailNotFound ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">This profile couldn't be found.</div>
            ) : wDetailError ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 text-sm">
                <p className="text-rose-600 dark:text-rose-400">{wDetailError}</p>
              </div>
            ) : !wSelectedId ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400 text-center px-6">
                Select a profile, or look one up by an exact identifier, to see its warehouse data.
              </div>
            ) : wDetailLoading || !wDetail ? (
              <div className="p-6 space-y-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ) : (
              <>
                <div className="px-6 pt-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      <Link2 size={18} />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={wDetail.profile.profile_id}>
                        {warehouseProfileName(wDetail.profile)}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {wDetail.profile.country || "—"} · Updated {formatWarehouseTimestamp(wDetail.profile.updated_at)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-1 px-6 mt-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
                  {(["overview", "traits", "events", "ids", "graph", "merges"] as WarehouseTabType[]).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setWActiveTab(tab)}
                      className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        wActiveTab === tab
                          ? "text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400"
                          : "text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200"
                      }`}
                    >
                      {tab === "ids" ? "External IDs" : tab === "graph" ? "Identity Graph" : tab === "merges" ? "Merge History" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {wActiveTab === "overview" && (
                    <div>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: "Gender", value: wDetail.profile.gender },
                          { label: "Age Group", value: wDetail.profile.age_group },
                          { label: "Location", value: [wDetail.profile.location_city, wDetail.profile.location_state].filter(Boolean).join(", ") || null },
                          { label: "Interest", value: wDetail.profile.interest },
                          { label: "Income Band", value: wDetail.profile.income_band },
                          { label: "Credit Score", value: wDetail.profile.credit_score },
                        ].map((f) => (
                          <div key={f.label} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{f.label}</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">{f.value ?? "—"}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Consents</div>
                        {wDetail.consents.length === 0 ? (
                          <p className="text-sm text-gray-400 italic">No consent records for this profile.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {wDetail.consents.map((c, i) => (
                              <span
                                key={`${c.scope}-${i}`}
                                className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                  c.granted
                                    ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                                }`}
                              >
                                {c.scope}: {c.granted ? "granted" : "not granted"} · {c.source}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {wActiveTab === "traits" && (
                    <div className="space-y-6">
                      {wDetail.traits.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">
                          No computed traits for this profile yet — most warehouse profiles don't have enriched traits computed.
                        </p>
                      ) : (
                        Object.entries(
                          wDetail.traits.reduce<Record<string, typeof wDetail.traits>>((groups, t) => {
                            const key = t.trait_type || "other";
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(t);
                            return groups;
                          }, {})
                        ).map(([groupKey, traits]) => (
                          <div key={groupKey}>
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{friendlyTraitLabel(groupKey)}</div>
                            <table className="w-full text-sm">
                              <tbody>
                                {traits.map((t) => (
                                  <tr key={t.trait_name} className="border-b border-gray-100 dark:border-gray-800">
                                    <td className="py-2 text-gray-500 dark:text-gray-400 w-1/2">{friendlyTraitLabel(t.trait_name)}</td>
                                    <td className="py-2 text-right">
                                      <p className="font-medium text-gray-900 dark:text-gray-100">{formatWarehouseValue(t.trait_value)}</p>
                                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5" title={t.model_version || ""}>
                                        {formatWarehouseTimestamp(t.computed_at)}
                                      </p>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {wActiveTab === "events" && (
                    <div>
                      {wEventsLoading || !wEvents ? (
                        <p className="text-sm text-gray-400">Loading events…</p>
                      ) : wEvents.data.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No normalized events for this profile.</p>
                      ) : (
                        <>
                          <div className="relative pl-5">
                            <div className="absolute left-[5px] top-1 bottom-1 w-0.5 bg-gray-200 dark:bg-gray-700" />
                            {wEvents.data.map((event) => (
                              <div key={event.event_id} className="relative pb-5">
                                <div className="absolute -left-5 top-1 w-2.5 h-2.5 rounded-full border-2 border-emerald-500 bg-white dark:bg-gray-900" />
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{event.event_name}</span>
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
                                    {event.event_category}
                                  </span>
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{event.source}</span>
                                </div>
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" title={formatWarehouseTimestamp(event.occurred_at)}>
                                  {relativeTime(event.occurred_at)} · {event.environment}
                                </div>
                                {Object.keys(event.properties || {}).length > 0 && (
                                  <pre className="text-[11px] mt-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto">
                                    {JSON.stringify(event.properties, null, 2)}
                                  </pre>
                                )}
                              </div>
                            ))}
                          </div>
                          {wEvents.total_pages > 1 && (
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800">
                              <button
                                type="button"
                                disabled={wEventsPage <= 1}
                                onClick={() => setWEventsPage((p) => Math.max(1, p - 1))}
                                className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Prev
                              </button>
                              <span>
                                Page {wEvents.page} of {wEvents.total_pages} · {wEvents.total} events
                              </span>
                              <button
                                type="button"
                                disabled={wEventsPage >= wEvents.total_pages}
                                onClick={() => setWEventsPage((p) => p + 1)}
                                className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {wActiveTab === "ids" && (
                    <div className="flex flex-wrap gap-3">
                      {wDetail.identifiers.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No identifiers on this profile.</p>
                      ) : (
                        wDetail.identifiers.map((id, i) => (
                          <div key={`${id.id_type}-${i}`} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                              {id.id_type}
                              {id.is_primary && <span className="text-blue-600 dark:text-blue-400">· primary</span>}
                            </div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{id.masked_value}</div>
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                              {id.source} · {Math.round(id.confidence * 100)}% confidence
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {wActiveTab === "graph" && (
                    <div>
                      {wGraphLoading || !wGraph ? (
                        <p className="text-sm text-gray-400">Loading identity graph…</p>
                      ) : wGraph.data.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No linked identity edges for this profile.</p>
                      ) : (
                        <div className="space-y-2">
                          {wGraph.data.map((edge) => (
                            <div key={edge.edge_id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 text-sm">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {edge.left.id_type}: {edge.left.masked_value}
                              </span>
                              <Link2 size={14} className="text-gray-400 shrink-0" />
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {edge.right.id_type}: {edge.right.masked_value}
                              </span>
                              <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                {edge.edge_strength} · {Math.round(edge.confidence * 100)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {wActiveTab === "merges" && (
                    <div>
                      {wMergesLoading || !wMerges ? (
                        <p className="text-sm text-gray-400">Loading merge history…</p>
                      ) : wMerges.data.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No merges recorded for this profile.</p>
                      ) : (
                        <div className="space-y-2">
                          {wMerges.data.map((m) => (
                            <div key={m.merge_id} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 text-sm">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">{m.member_count} profiles merged</span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {m.algorithm_version} · {Math.round(m.confidence * 100)}% confidence
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatWarehouseTimestamp(m.created_at)}</div>
                              {m.reversed_at && (
                                <div className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                                  Reversed {formatWarehouseTimestamp(m.reversed_at)}
                                  {m.reversed_reason ? `: ${m.reversed_reason}` : ""}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ProfileExplorer;
